import React from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Checkbox,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import type { CustomRenewalPreview } from '@homevest/types/renewals'
import { money, monthLabel } from './RenewalBillingFields'

export function RenewalRecurringFeesReview({
  preview,
  requestedOptionPremium
}: {
  preview: CustomRenewalPreview
  requestedOptionPremium?: number
}) {
  return (
    <Box borderWidth='1px' borderRadius='lg' p={5}>
      <Text fontWeight='semibold' mb={3}>
        Recurring fees
      </Text>
      {requestedOptionPremium !== undefined && (
        <Text mb={2}>Monthly option premium · {money(requestedOptionPremium)}/month</Text>
      )}
      {preview.internet && (
        <Text mb={2}>
          Internet:{' '}
          {preview.internet.mode === 'waived'
            ? 'Waived — $0.00/month'
            : `${money(preview.internet.effectiveAmount)}/month`}
          {preview.internet.restoring && ' (normal fee restored)'}
          {preview.internet.mode === 'default' &&
            preview.internet.effectiveAmount > 0 &&
            !preview.internet.restoring &&
            !preview.internet.addOns.length &&
            ' (starts at renewal)'}
        </Text>
      )}
      {preview.addOns
        .filter((addOn) => !addOn.isInternet || addOn.scheduled)
        .map((addOn) => (
          <Box key={addOn.id} mb={2}>
            <Text>
              {addOn.name} · {money(addOn.price)}/month ·{' '}
              {addOn.removed
                ? 'Removed at renewal'
                : addOn.scheduled
                ? 'Scheduled change'
                : 'Retained'}
            </Text>
            <Text fontSize='sm' color='gray.600'>
              Starts {addOn.startDate} ·{' '}
              {addOn.endDate ? `Currently ends ${addOn.endDate}` : 'No scheduled end date'}
            </Text>
          </Box>
        ))}
      {!preview.addOns.length && !preview.internet && requestedOptionPremium === undefined && (
        <Text color='gray.500'>None</Text>
      )}
      {preview.addOns.some((addOn) => addOn.removed) && (
        <Text fontSize='sm' color='gray.600'>
          Removed fees remain ended until manually restored.
        </Text>
      )}
    </Box>
  )
}

export default function RenewalBillingReview({
  preview,
  requestedOptionPremium,
  acknowledged,
  setAcknowledged
}: {
  preview: CustomRenewalPreview
  requestedOptionPremium?: number
  acknowledged: boolean
  setAcknowledged: (acknowledged: boolean) => void
}) {
  return (
    <Stack spacing={5}>
      <Box borderWidth='1px' borderRadius='lg' p={5}>
        <Text fontWeight='semibold' mb={3}>
          Rent concessions · {money(preview.concessions.total)} total
        </Text>
        {preview.concessions.credits.length ? (
          <>
            <Text whiteSpace='pre-wrap' mb={3}>
              {preview.concessions.description}
            </Text>
            {preview.concessions.credits.map((credit) => (
              <Text key={credit.month}>
                {monthLabel(credit.month)}: {money(credit.amount)} Rent credit, dated {credit.date}
              </Text>
            ))}
          </>
        ) : (
          <Text color='gray.500'>No Rent concessions.</Text>
        )}
        <Text fontSize='sm' color='gray.600' mt={3}>
          Concessions are applied in full, including partial months. Billing changes begin only
          after the renewal is fully signed.
        </Text>
      </Box>
      <Box borderWidth='1px' borderRadius='lg' p={5}>
        <Text fontWeight='semibold' mb={3}>
          Monthly payment schedule
        </Text>
        {requestedOptionPremium !== undefined &&
          preview.months.length > 0 &&
          preview.months[0]?.optionPremium !== requestedOptionPremium && (
            <Alert status='warning' alignItems='start' mb={3}>
              <AlertIcon />
              <AlertDescription>
                The agreement lists {money(requestedOptionPremium)}/month for investment / option
                charges. Saved billing remains {money(preview.months[0].optionPremium)}/month; this
                renewal does not change it. Payments below use saved billing. Update rental billing
                separately if needed.
              </AlertDescription>
            </Alert>
          )}
        <TableContainer>
          <Table size='sm'>
            <Thead>
              <Tr>
                <Th>Month</Th>
                <Th isNumeric>Rent</Th>
                <Th isNumeric>Investment / option</Th>
                <Th isNumeric>Fees</Th>
                <Th isNumeric>Rent credit</Th>
                <Th isNumeric>Payment</Th>
              </Tr>
            </Thead>
            <Tbody>
              {preview.months.map((month) => (
                <Tr key={month.month}>
                  <Td>{monthLabel(month.month)}</Td>
                  <Td isNumeric>{money(month.rent)}</Td>
                  <Td isNumeric>{money(month.optionPremium)}</Td>
                  <Td isNumeric>{money(month.fees)}</Td>
                  <Td isNumeric>{money(month.concession)}</Td>
                  <Td isNumeric>{money(month.total)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <Text color='gray.600' fontSize='sm' mt={3}>
          Charges reflect scheduled dates and proration. Rent credits are not prorated.
        </Text>
      </Box>
      {preview.postedCharges.length > 0 && (
        <Alert status='warning' alignItems='start'>
          <AlertIcon />
          <Box>
            <Text fontWeight='semibold'>Posted charges need manual correction</Text>
            <AlertDescription>
              These charges will not be reversed or refunded by removing the fee.
              {preview.postedCharges.map((charge) => (
                <Text key={charge.id}>
                  {charge.date} · {charge.liabilityTypeName} · {money(charge.amount)}
                </Text>
              ))}
            </AlertDescription>
            <Checkbox
              mt={3}
              isChecked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            >
              I understand these posted charges need separate manual correction.
            </Checkbox>
          </Box>
        </Alert>
      )}
    </Stack>
  )
}
